import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    getOperators
} from "../../slices/operators/thunk";
import { setOperatorFilters } from "../../slices/operators/reducer";
import { Button, Card, CardBody, Badge, Modal, ModalHeader, ModalBody } from "reactstrap";
import CommonDataTable from "../../common/DataTable";
import CreateOperatorModal from "./CreateOperatorModal";
import OperatorUsersModal from "./OperatorUsersModal";
import StatusUpdateModal from "./StatusUpdateModal";
import WinMultiplierModal from "../Users/WinMultiplierModal";
import StopLossModal from "./StopLossModal";
import { formatDateTimeDDMMYYYY } from "../../helpers/dateUtils";

const OPERATOR_GAMES = [
    { key: 'lt', label: 'LT' },
    { key: 'lt1', label: 'LT1' },
    { key: 'lt2', label: 'LT2', fallbackKey: 'lt3' },
    { key: 'ro', label: 'RO' },
    { key: 'so', label: 'SO' }
];

const Operators = () => {
    const dispatch = useDispatch();

    const {
        operators,
        loading,
        pagination,
        filters
    } = useSelector((state) => state.Operators);

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showUsersModal, setShowUsersModal] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [showMultiplierModal, setShowMultiplierModal] = useState(false);
    const [showStopLossModal, setShowStopLossModal] = useState(false);
    const [operatorToEdit, setOperatorToEdit] = useState(null);
    const [selectedOperator, setSelectedOperator] = useState(null);
    const isMounted = useRef(true);

    const [masterOperators, setMasterOperators] = useState([]);

    // Keep all loaded operators in masterOperators so search never loses them
    useEffect(() => {
        if (operators && operators.length > 0) {
            setMasterOperators(prev => {
                if (operators.length >= prev.length || !filters?.search) {
                    return operators;
                }
                return prev;
            });
        }
    }, [operators, filters?.search]);

    const fetchOperators = useCallback(() => {
        if (isMounted.current) {
            // Always fetch complete operator list without search to avoid remote backend 0-results on account numbers
            dispatch(getOperators({ page: 1, limit: 100 }));
        }
    }, [dispatch]);

    useEffect(() => {
        fetchOperators();
    }, [fetchOperators]);

    const updateFilterParams = useCallback((newParams) => {
        dispatch(setOperatorFilters(newParams));
    }, [dispatch]);

    // Resilient search filtering across both username and acnumber
    const sourceList = masterOperators.length > 0 ? masterOperators : (operators || []);

    const filteredOperators = React.useMemo(() => {
        if (!filters?.search || !filters.search.trim()) {
            return sourceList;
        }
        const term = filters.search.trim().toLowerCase();
        return sourceList.filter((op) => {
            const username = (op.username || '').toLowerCase();
            const acnumber = (op.acnumber || '').toLowerCase();
            return username.includes(term) || acnumber.includes(term);
        });
    }, [sourceList, filters?.search]);

    const perPage = filters?.limit || filters?.perPage || 10;
    const currentPage = filters?.page || filters?.currentPage || 1;
    const totalCount = filteredOperators.length;

    const displayOperators = React.useMemo(() => {
        if (totalCount <= perPage) return filteredOperators;
        const start = (currentPage - 1) * perPage;
        return filteredOperators.slice(start, start + perPage);
    }, [filteredOperators, currentPage, perPage, totalCount]);

    const handleCreate = () => {
        setOperatorToEdit(null);
        setShowCreateModal(true);
    };

    const handleEdit = (operator) => {
        setOperatorToEdit(operator);
        setShowCreateModal(true);
    };

    const handleViewUsers = (operator) => {
        setSelectedOperator(operator);
        setShowUsersModal(true);
    };

    const handleStatusClick = (operator, e) => {
        // Prevent row selection or other events
        e?.stopPropagation();
        setSelectedOperator(operator);
        setShowStatusModal(true);
    };

    const columns = [
        {
            name: "Username",
            selector: row => row.username,
        },
        {
            name: "Account Number",
            selector: row => row.acnumber || '-',
        },

        {
            name: "Status",
            selector: row => row.isActive,
            cell: row => (
                <div
                    onClick={(e) => handleStatusClick(row, e)}
                    style={{ cursor: 'pointer', pointerEvents: 'auto' }} // Ensure clickable even if row is disabled
                    title="Click to update status"
                >
                    <Badge
                        color={row.isActive ? "success" : "danger"}
                        className="badge-border"
                    >
                        {row.isActive ? "Active" : "Inactive"}
                    </Badge>
                </div>
            )
        },
        {
            name: "Win Rate",
            selector: row => row.winMultiplier ? 'Set' : 'None',
            cell: row => (
                <div
                    className="d-flex gap-1 flex-wrap align-items-center py-1"
                    onClick={() => {
                        setSelectedOperator(row);
                        setShowMultiplierModal(true);
                    }}
                    style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                    title="Click to set win multipliers"
                    onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                    {OPERATOR_GAMES.map(({ key, label, fallbackKey }) => {
                        const mult = row.winMultiplier?.[key] ?? (fallbackKey ? row.winMultiplier?.[fallbackKey] : undefined);
                        return (
                            <div
                                key={key}
                                className={`px-2 py-1 rounded border fs-10 fw-bold ${mult ? 'bg-primary-subtle text-primary border-primary-subtle' : 'bg-light text-muted border-dashed'}`}
                                style={{ minWidth: '60px', textAlign: 'center', borderRadius: '5px' }}
                            >
                                {label}: {mult ? `${mult}x` : '-'}
                            </div>
                        );
                    })}
                </div>
            )
        },
        {
            name: "Stop Loss",
            selector: row => row.stopLoss ? 'Set' : 'None',
            cell: row => (
                <div
                    className="d-flex gap-1 flex-wrap align-items-center py-1"
                    onClick={() => {
                        setSelectedOperator(row);
                        setShowStopLossModal(true);
                    }}
                    style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                    title="Click to set dynamic stop loss"
                    onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                    {OPERATOR_GAMES.map(({ key, label, fallbackKey }) => {
                        const sl = row.stopLoss?.[key] ?? (fallbackKey ? row.stopLoss?.[fallbackKey] : undefined);
                        return (
                            <div
                                key={key}
                                className={`px-2 py-1 rounded border fs-10 fw-bold ${sl != null ? 'bg-danger-subtle text-danger border-danger-subtle' : 'bg-light text-muted border-dashed'}`}
                                style={{ minWidth: '60px', textAlign: 'center', borderRadius: '5px' }}
                            >
                                {label}: {sl != null ? `${sl.toLocaleString()}` : '-'}
                            </div>
                        );
                    })}
                </div>
            )
        },
        {
            name: "Created At",
            selector: row => row.createdAt,
            cell: row => formatDateTimeDDMMYYYY(row.createdAt)
        },
        {
            name: "Actions",
            cell: row => (
                <div className="d-flex gap-2">
                    <Button
                        color="info"
                        size="sm"
                        onClick={() => handleViewUsers(row)}
                        title="View Users"
                        disabled={!row.isActive}
                    >
                        <i className="ri-team-line"></i>
                    </Button>
                    <Button
                        color="primary"
                        size="sm"
                        onClick={() => handleEdit(row)}
                        title="Edit Operator"
                        disabled={!row.isActive}
                    >
                        <i className="ri-pencil-line"></i>
                    </Button>
                </div>
            ),
            ignoreRowClick: true
        }
    ];

    // Style for inactive rows
    const conditionalRowStyles = [
        {
            when: row => !row.isActive, // If inactive
            style: {
                // opacity: 0.6, // Removed to keep badge vivid
                backgroundColor: '#f0f0f0', // Light background
                color: '#b0b0b0', // Fade text instead
                pointerEvents: 'none', // Non clickable row
            },
        },
    ];

    return (
        <div className="page-content">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="page-title-box d-flex align-items-center justify-content-between">
                            <h4 className="mb-0">Operators Management</h4>
                        </div>
                    </div>
                </div>

                <CommonDataTable
                    columns={columns}
                    data={displayOperators}
                    loading={loading}
                    totalRows={totalCount}
                    filterParams={filters}
                    fetchData={updateFilterParams}
                    searchEnable={true}
                    showAddButton={true}
                    addOnClickMethod={handleCreate}
                    buttonName="Create Operator"
                    showExportButton={false}
                    checkboxEnabled={false}
                    showRefreshButton={true}
                    onRefresh={fetchOperators}
                    refreshLoading={loading}
                    conditionalRowStyles={conditionalRowStyles}
                />

                <CreateOperatorModal
                    isOpen={showCreateModal}
                    toggle={() => setShowCreateModal(false)}
                    operatorToEdit={operatorToEdit}
                />

                <OperatorUsersModal
                    isOpen={showUsersModal}
                    toggle={() => setShowUsersModal(false)}
                    operator={selectedOperator}
                />

                <StatusUpdateModal
                    isOpen={showStatusModal}
                    toggle={() => setShowStatusModal(false)}
                    operator={selectedOperator}
                />

                <WinMultiplierModal
                    isOpen={showMultiplierModal}
                    toggle={() => setShowMultiplierModal(false)}
                    target={selectedOperator}
                    isOperator={true}
                />

                <StopLossModal
                    isOpen={showStopLossModal}
                    toggle={() => setShowStopLossModal(false)}
                    operator={selectedOperator}
                />
            </div>
        </div>
    );
};

export default Operators;
